import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Users, FileText, CalendarDays, FolderOpen, HardHat, Receipt,
  LogOut, Settings, User, X, LayoutDashboard, ChevronUp,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { BUILD_VERSION } from '@/version';
import gpsLogo from '@/GPS-logo-2022.svg';

// Nav-Einträge mit zugehörigem Modul; deaktivierte Module werden
// herausgefiltert. Dashboard + "Anträge" gehören zum Konto, nicht
// zu einem Modul.
const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, module: null as string | null },
  { name: 'Bauvorhaben', href: '/bauvorhaben', icon: HardHat, module: 'bauvorhaben' },
  { name: 'Rechnungen', href: '/rechnungen', icon: Receipt, module: 'rechnungen' },
  { name: 'Dokumente', href: '/dokumente', icon: FolderOpen, module: 'dokumente' },
  { name: 'Kontakte', href: '/contacts', icon: Users, module: 'kontakte' },
  { name: 'Kalender', href: '/calendar', icon: CalendarDays, module: 'kalender' },
  { name: 'Anträge', href: '/proposals', icon: FileText, module: null as string | null },
];

interface SidebarProps {
  variant: 'rail' | 'drawer';
  onNavigate?: () => void;
}

/**
 * Linke Menüleiste: ein Component, zwei Varianten — rail (docked ≥ md)
 * und drawer (Overlay < md). Enthält die Modul-gefilterte Navigation,
 * Admin-Sektion, Profil, Abmelden und die Build-Version.
 */
export function Sidebar({ variant, onNavigate }: SidebarProps) {
  const { user, logout, isApprover, hasModule } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const items = navigation.filter((item) => !item.module || hasModule(item.module));

  const isActive = (href: string) => {
    // Dashboard liegt exakt auf "/" — /contacts gehört jetzt zu Kontakte.
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => {
    onNavigate?.();
    logout();
  };

  const handleAccountNavigate = (path: string) => {
    setAccountMenuOpen(false);
    onNavigate?.();
    navigate(path);
  };

  return (
    <aside
      className={
        variant === 'rail'
          ? 'hidden md:flex w-56 shrink-0 flex-col border-r border-gray-200 bg-white'
          : 'md:hidden fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-white shadow-xl'
      }
    >
      {/* Logo (+ Schließen-Button im Drawer) */}
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-100 px-4 pt-[env(safe-area-inset-top)]">
        <Link to="/" className="flex items-center focus:outline-none" onClick={onNavigate}>
          <img src={gpsLogo} alt="GPS" className="h-8 w-auto" />
        </Link>
        {variant === 'drawer' && (
          <button
            type="button"
            onClick={onNavigate}
            className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            title="Menü schließen"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-1 px-2">
          {items.map((item) => (
            <li key={item.name}>
              <Link
                to={item.href}
                onClick={onNavigate}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium ${
                  isActive(item.href)
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <item.icon className={`h-4 w-4 shrink-0 ${isActive(item.href) ? 'text-primary-600' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            </li>
          ))}
        </ul>

      </nav>

      {/* unten: Benutzerblock (öffnet das Konto-Menü: Profil /
          Systemeinstellungen — /admin enthält Benutzer, Module,
          Einstellungen) + Abmelden + Version */}
      <div className="relative shrink-0 border-t border-gray-100 p-3">
        {accountMenuOpen && (
          <div className="fixed inset-0 z-40" onClick={() => setAccountMenuOpen(false)} />
        )}
        <div className="relative">
          <button
            type="button"
            onClick={() => setAccountMenuOpen((open) => !open)}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-gray-100"
            title="Konto-Menü"
          >
            <div className="h-8 w-8 shrink-0 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-primary-600 font-medium text-sm">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900">{user?.name}</p>
              <span
                className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                  user?.role === 'admin'
                    ? 'bg-purple-100 text-purple-800'
                    : user?.role === 'approver'
                      ? 'bg-blue-100 text-blue-800'
                      : user?.role === 'vertrieb'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-800'
                }`}
              >
                {user?.role === 'admin'
                  ? 'Administrator'
                  : user?.role === 'approver'
                    ? 'Genehmiger'
                    : user?.role === 'vertrieb'
                      ? 'Vertrieb'
                      : 'Benutzer'}
              </span>
            </div>
            <ChevronUp
              className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${accountMenuOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {accountMenuOpen && (
            <div className="absolute bottom-full left-0 right-0 z-50 mb-2 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
              <button
                type="button"
                onClick={() => handleAccountNavigate('/profile')}
                className={`flex w-full items-center gap-3 px-3 py-2 text-sm font-medium ${
                  location.pathname.startsWith('/profile')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <User className="h-4 w-4 shrink-0 text-gray-400" />
                Profil
              </button>
              {isApprover && (
                <button
                  type="button"
                  onClick={() => handleAccountNavigate('/admin')}
                  className={`flex w-full items-center gap-3 px-3 py-2 text-sm font-medium ${
                    location.pathname.startsWith('/admin')
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Settings className="h-4 w-4 shrink-0 text-gray-400" />
                  Systemeinstellungen
                </button>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="mt-1 flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
        >
          <LogOut className="h-4 w-4" />
          Abmelden
        </button>
        <p className="select-none pb-1 pt-2 text-center text-[10px] text-gray-400">v{BUILD_VERSION}</p>
      </div>
    </aside>
  );
}